import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  Block,
  ExpanderContent,
  ExpanderGroup,
  ExpanderTitleButton,
  Paragraph,
} from 'suomifi-ui-components'
import LocalizedString from 'todistukset-ui/src/vtj/components/localized-string/LocalizedString.react'
import {
  ContentBlock,
  ContentSection,
  StyledExpander,
  TextBlockWithRows,
  Heading,
} from 'todistukset-ui/src/vtj/components/page-content/PageContent.sc'

type QuestionTranslationKeyPair = {
  title: string
  content: string
}

function expandToQaPairs(
  questionKeyParts: (
    | string
    | { titleKeyPart: string; contentKeyPart: string }
  )[]
): QuestionTranslationKeyPair[] {
  return questionKeyParts.map((questionKeyPart): QuestionTranslationKeyPair => {
    const { titleKeyPart, contentKeyPart } =
      typeof questionKeyPart === 'string'
        ? {
            titleKeyPart: questionKeyPart,
            contentKeyPart: questionKeyPart,
          }
        : questionKeyPart
    return {
      title: `infoPageQuestionTitle_${titleKeyPart}`,
      content: `infoPageQuestionContent_${contentKeyPart}_html`,
    }
  })
}

// prettier-ignore
const faqContentKeys = {
  ordering: expandToQaPairs([
    'ordering_200',
    'ordering_250',
    'ordering_300_aet',
    'ordering_325_aet',
    'ordering_350',
    'ordering_400',
    'ordering_450_aet',
    'ordering_475',
    'ordering_500_aet',
    'ordering_550_aet',
    'ordering_650_aet',
    'ordering_675_aet',
    'ordering_700',
    'ordering_750',
  ]),
  delivery: expandToQaPairs([
    'delivery_200_aet',
    'delivery_300_aet',
    { titleKeyPart: 'delivery_500', contentKeyPart: 'delivery_500_suomifi_disabled' },
    'delivery_800',
  ]),
  substance: expandToQaPairs([
    'substance_200_aet',
    'substance_250',
    'substance_350',
    'substance_400',
    'substance_450',
    'substance_500',
    'substance_600_aet',
    'substance_650_aet',
    'substance_700_aet',
    'substance_800_aet',
    'substance_900_aet',
    'substance_950',
  ]),
  security: expandToQaPairs([
    'security_010_aet',
    'security_020',
    'security_030',
    'security_040',
    'security_050',
    'security_060',
  ]),
}

function QuestionExpanders({
  keyPairs,
}: {
  keyPairs: QuestionTranslationKeyPair[]
}) {
  const { t } = useTranslation()
  return (
    <>
      {keyPairs.map(({ title, content }) => {
        return (
          <StyledExpander key={title}>
            <ExpanderTitleButton data-test-id={`${title}-button`}>
              <>{t(title)}</>
            </ExpanderTitleButton>
            <ExpanderContent>
              <>
                <Block variant={'section'}>
                  <TextBlockWithRows>
                    <LocalizedString stringKey={content} />
                  </TextBlockWithRows>
                </Block>
              </>
            </ExpanderContent>
          </StyledExpander>
        )
      })}
    </>
  )
}

function InfoPage(): React.JSX.Element {
  const { t } = useTranslation()

  // Instead of providing exact translation keys for each question, the question title and answer content
  // are numbered so they can more easily be added, removed, reordered etc. The content people will still need
  // to contact the dev team to add or remove keys.
  const expanderGroupProps = {
    openAllText: t('landingPageOpenAllButton'),
    ariaOpenAllText: t('landingPageOpenAllButtonAria'),
    closeAllText: t('landingPageCloseAllButton'),
    ariaCloseAllText: t('landingPageCloseAllButtonAria'),
  }

  return (
    <ContentBlock
      variant={'section'}
      padding={'xl'}
      pr={'xxl'}
      data-test-id={'info-page-container'}
    >
      <ContentSection>
        <Heading variant="h2">
          <LocalizedString stringKey="navigationTabsFaqTitle" />
        </Heading>
        <Paragraph>
          <LocalizedString stringKey="infoPageDescription" />
        </Paragraph>
      </ContentSection>

      <ContentSection>
        <Heading
          id="todistuksen-tilaaminen"
          variant={'h3'}
          data-test-id={'infoPageTitle'}
        >
          <>{t('infoPageSubTitleOrdering')}</>
        </Heading>
        <ExpanderGroup {...expanderGroupProps}>
          <QuestionExpanders keyPairs={faqContentKeys.ordering} />
        </ExpanderGroup>
      </ContentSection>

      <ContentSection>
        <Heading
          id="todistuksen-toimitus"
          variant={'h3'}
          data-test-id={'infoPageTitle'}
        >
          <>{t('infoPageSubTitleDelivery')}</>
        </Heading>
        <ExpanderGroup {...expanderGroupProps}>
          <QuestionExpanders keyPairs={faqContentKeys.delivery} />
        </ExpanderGroup>
      </ContentSection>

      <ContentSection>
        <Heading
          id="todistuksen-tietosisallot"
          variant={'h3'}
          data-test-id={'infoPageTitle'}
        >
          <>{t('infoPageSubTitleSubstance')}</>
        </Heading>
        <ExpanderGroup {...expanderGroupProps}>
          <QuestionExpanders keyPairs={faqContentKeys.substance} />
        </ExpanderGroup>
      </ContentSection>

      <ContentSection>
        <Heading id="tietosuoja" variant={'h3'} data-test-id={'infoPageTitle'}>
          <>{t('infoPageSubTitleSecurity')}</>
        </Heading>
        <ExpanderGroup {...expanderGroupProps}>
          <QuestionExpanders keyPairs={faqContentKeys.security} />
        </ExpanderGroup>
      </ContentSection>
    </ContentBlock>
  )
}

export default InfoPage
